import { ReactNode } from 'react';
import {
    Box,
    BoxProps,
    FormControl,
    MenuItem,
    MenuProps,
    Select,
    SelectProps,
    Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { KeyboardArrowDown as ChevronDownIcon } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";

type DropdownOption = {
    label: string;
    value: string;
    icon?: string;
    isDisabled?: boolean;
};

type CustomDropdownProps = {
    dropDownPlaceholder?: string;
    value: string;
    boldSelectedValue?: boolean;
    handleChange: (event: any) => void;
    boxProps?: BoxProps;
    menuProps?: Partial<MenuProps>;
    selectComponentProps?: SelectProps;
    noSelectionLabel?: string;
    dropdownIcon?: React.ElementType;
    disabled?: boolean;
    dropDownStyle?: any;
    className?: any;
} & ({ options: DropdownOption[]; children?: never } | { children: ReactNode; options?: never });

const CustomSelectDropdown = ({
    value,
    options,
    handleChange,
    dropDownPlaceholder,
    boxProps,
    menuProps,
    selectComponentProps,
    noSelectionLabel,
    boldSelectedValue = false,
    children,
    dropdownIcon,
    disabled,
    dropDownStyle,
    className
}: CustomDropdownProps) => {
    const { sx, ...props } = boxProps || {};
    const theme = useTheme();
    const colors = theme.palette;

    const SKY_BLUE = colors.DropdownV2.borderColorHover;
    const LIGHT_BLUE_BG = colors.DropdownV2.background;
    const HEADER_TEXT = colors.DropdownV2.menuItemText;
    const MUTED_TEXT = colors.DropdownV2.mutedText;

    const dropdownSx = {
        height: '32px',
        backgroundColor: LIGHT_BLUE_BG,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: MUTED_TEXT,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: SKY_BLUE,
        },
        fontSize: '14px',
        borderRadius: '4px',
        '& .MuiSelect-select': {
            color: HEADER_TEXT,
            fontWeight: 500,
        },
    };

    return (
        <Box
            sx={{
                backgroundColor: LIGHT_BLUE_BG,
                borderRadius: '4px',
                zIndex: 9,
                minWidth: '90px',
                textAlign: 'left',
                height: 'fit-content',
                ...sx
            }}
            className='custom-dropdown-box'
            {...props}
        >
            <FormControl fullWidth>
                <Select
                    disabled={disabled}
                    value={value}
                    style={dropDownStyle}
                    onChange={handleChange}
                    placeholder={dropDownPlaceholder}
                    IconComponent={dropdownIcon ? dropdownIcon : ChevronDownIcon}
                    MenuProps={{
                        ...menuProps,
                        PaperProps: {
                            ...menuProps?.PaperProps,
                            sx: {
                                minWidth: className === 'total-cost-spends-dropdown'
                                    ? 'max-content'
                                    : undefined,
                                borderRadius: '4px',
                                mt: 0.5,
                                backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                                color: `${colors.DropdownV2.menuItemText} !important`,
                                boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
                                border: '1px solid #E0E4EC !important',
                                ...(menuProps?.PaperProps?.sx),
                            },
                        },
                        MenuListProps: {
                            ...menuProps?.MenuListProps,
                            sx: {
                                paddingTop: 0,
                                paddingBottom: 0,
                                ...(menuProps?.MenuListProps?.sx),
                            },
                        },
                    }}
                    className={className}
                    {...(!!options && {
                        renderValue: (selected) => {
                            if (!selected) {
                                return <em>{noSelectionLabel}</em>;
                            }

                            const selectedOption = options.find((option) => option.value === selected);

                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!!selectedOption?.icon && (
                                        <Box component="img" src={selectedOption.icon} alt="" sx={{ mr: 1 }} />
                                    )}
                                    {selectedOption?.label}
                                </Box>
                            );
                        }
                    })}
                    {...selectComponentProps}
                    sx={{
                        ...dropdownSx,
                        backgroundColor: disabled
                            ? `${colors.dropdown.bgColorDisabled} !important`
                            : LIGHT_BLUE_BG,
                        ...selectComponentProps?.sx,

                        '& .MuiSelect-select': {
                            ...(selectComponentProps?.sx as any)?.['& .MuiSelect-select'],
                            color: `${HEADER_TEXT} !important`,
                            fontWeight: 500,
                        },

                        '& .MuiSelect-icon': {
                            color: SKY_BLUE,
                            fontSize: '24px',
                            right: '12px',
                        }
                    }}
                >
                    {!!options
                        ? options.flatMap(({ label, value: optionValue, icon, isDisabled = false }, index) => {
                            const elements = [];
                            const isSelected = value === optionValue;

                            if (index === 1 && optionValue === 'commitAll') {
                                elements.push(
                                    <Box
                                        key={`separator-${index}`}
                                        sx={{
                                            height: '4px',
                                            backgroundColor: '#003771',
                                        }}
                                    />
                                );
                            }

                            elements.push(
                                <MenuItem
                                    key={optionValue}
                                    value={optionValue}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (optionValue === value && ['commit', 'commitAll'].includes(optionValue)) {
                                            handleChange({ target: { value: optionValue } });
                                        }
                                    }}
                                    sx={{
                                        fontSize: '14px',
                                        fontFamily: 'Montserrat',
                                        fontWeight: isSelected ? 500 : 400,
                                        py: className === 'total-cost-spends-dropdown' ? '6px' : 1.25,
                                        px: className === 'total-cost-spends-dropdown' ? '16px' : 2,
                                        minHeight: className === 'total-cost-spends-dropdown'
                                            ? 'unset'
                                            : undefined,
                                        backgroundColor: isSelected ? `${colors.DropdownV2.menuItemSelectedBg} !important` : 'transparent',
                                        color:  `${colors.DropdownV2.menuItemText} !important`,
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',

                                        '&.Mui-disabled': {
                                            opacity: 0.7,
                                            color:  `${colors.DropdownV2.menuItemText} !important`,
                                            backgroundColor: isSelected ? `${colors.DropdownV2.menuItemSelectedBg} !important` : 'transparent',
                                        },

                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                                            color:  `${colors.DropdownV2.menuItemText} !important`,
                                        },

                                        '&.Mui-selected:hover': {
                                            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                                            color:  `${colors.DropdownV2.menuItemText} !important`,
                                        },

                                        '&:hover': {
                                            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                                            color:  `${colors.DropdownV2.menuItemText} !important`,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {!!icon && (
                                            <Box component="img" src={icon} alt="" sx={{ mr: 1 }} />
                                        )}

                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontFamily: 'Montserrat',
                                                fontWeight: 'inherit'
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>

                                    {isSelected && (
                                        <CheckIcon sx={{ fontSize: '18px', color: '#1A5A96' }} />
                                    )}
                                </MenuItem>
                            );

                            return elements;
                        })
                        : children}
                </Select>
            </FormControl>
        </Box>
    );
};

export default CustomSelectDropdown;